import React, { useEffect, useRef, memo, useMemo, useState, useContext } from "react";
import { RemixBuilderContext } from "../remix/shared/RemixBuilderContext";

const TemplatePreview = memo((props) => {
  const template = props.template;
  const contentRef = useRef(null);
  const wrapperRef = useRef(null);
  const timeoutRef = useRef(null);
  const lastRequestTimeRef = useRef(0);
  const resizeObserverRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [contextData] = useContext(RemixBuilderContext);
  const options = props.options || {};

  const buildPreviewUrl = useMemo(() => {
    if (!template) return null;
    if (!template.object_type || template.object_id == null || template.object_id === undefined) {
      return null;
    }

    const root_url = window.location.origin;
    const targetController = {
      Cta: "cta",
      Section: "sections",
      Step: "steps",
      Element: "elements",
      Field: "fields",
    }[template.object_type];

    if (!targetController) return null;

    let src = `${root_url}/websites/0/${targetController}/${template.object_id}/preview`;

    // Use remix_mappings to properly map styles
    const remixMappings = contextData?.remix_mappings || {};
    let parsed_options = {};

    // Apply heading font family mappings
    if (options.styles?.["heading-font-family"] && remixMappings['heading-font-family']) {
      remixMappings['heading-font-family'].forEach(option => {
        parsed_options[option] = options.styles["heading-font-family"]?.value || options.styles["heading-font-family"];
      });
    }

    // Apply heading font color mappings
    if (options.styles?.["heading-font-color"] && remixMappings['heading-font-color']) {
      remixMappings['heading-font-color'].forEach(option => {
        parsed_options[option] = options.styles["heading-font-color"];
      });
    }

    // Apply paragraph font family mappings
    if (options.styles?.["paragraph-font-family"] && remixMappings['paragraph-font-family']) {
      remixMappings['paragraph-font-family'].forEach(option => {
        parsed_options[option] = options.styles["paragraph-font-family"]?.value || options.styles["paragraph-font-family"];
      });
    }

    // Apply paragraph font color mappings
    if (options.styles?.["paragraph-font-color"] && remixMappings['paragraph-font-color']) {
      remixMappings['paragraph-font-color'].forEach(option => {
        parsed_options[option] = options.styles["paragraph-font-color"];
      });
    }

    // Apply button color mappings
    if (options.styles?.["button-color"] && remixMappings['button-color']) {
      remixMappings['button-color'].forEach(option => {
        parsed_options[option] = options.styles["button-color"];
      });
    }

    // Apply button text color mappings (fallback to white for older styles)
    const buttonTextColor = options.styles?.["button-text-color"] || (options.styles?.["button-color"] ? "white" : null);
    if (buttonTextColor && remixMappings['button-text-color']) {
      remixMappings['button-text-color'].forEach(option => {
        parsed_options[option] = buttonTextColor;
      });
    }

    // Apply accent color mappings
    if (options.styles?.["accent-color"] && remixMappings['accent-color']) {
      remixMappings['accent-color'].forEach(option => {
        parsed_options[option] = options.styles["accent-color"];
      });
    }

    // Apply logo style mappings
    if (options.styles?.["logo-style"] && remixMappings['logo-style']) {
      parsed_options['logo-style'] = options.styles["logo-style"];
    }

    // Apply logo image mappings
    if (options.styles?.["logo-image"] && remixMappings['logo-image']) {
      parsed_options['logo-image'] = options.styles["logo-image"];
    }

    if (options) {
      src += `?${new URLSearchParams({
        variant: JSON.stringify({ options: parsed_options }),
      }).toString()}`;
    }

    return src;
  }, [template?.id, options]);

  const updateWrapperHeight = (contentEl, wrapperEl, scaleFactor) => {
    if (!contentEl || !wrapperEl) return;

    const containerHeight = contentEl.scrollHeight;
    const scaledHeight = containerHeight * scaleFactor;
    const minHeight = props.children ? 50 : 30;
    const buffer = 8; // Prevent cutoff from rounding or sub-pixel rendering

    wrapperEl.style.height = `${Math.max(scaledHeight, minHeight) + buffer}px`;
  };

  const applyPreviewLayout = (contentEl, wrapperEl) => {
    if (!contentEl || !wrapperEl) return;

    const contentContainer = wrapperEl.children[0];
    if (contentContainer) {
      contentContainer.style.width = "1300px";
    }
    // Force reflow so content dimensions are correct before measuring
    void contentEl.offsetHeight;

    const containerHeight = contentEl.scrollHeight;
    const containerWidth = wrapperEl.clientWidth;
    const contentWidth = contentEl.scrollWidth || 1400;

    let scaleFactor;
    if (containerWidth < 700) {
      scaleFactor = Math.min(0.5, (containerWidth - 50) / contentWidth);
    } else if (containerWidth < 1000) {
      scaleFactor = Math.min(0.6, (containerWidth - 50) / contentWidth);
    } else if (containerWidth < 1400) {
      scaleFactor = Math.min(0.8, (containerWidth - 80) / contentWidth);
    } else {
      scaleFactor = Math.min(1.0, (containerWidth - 100) / contentWidth);
    }
    scaleFactor = Math.max(0.02, scaleFactor);

    if (contentContainer) {
      contentContainer.style.left = "50%";
      contentContainer.style.top = "0";
      contentContainer.style.transform = `translateX(-50%) scale(${scaleFactor})`;
      contentContainer.style.transformOrigin = "top center";
    }

    updateWrapperHeight(contentEl, wrapperEl, scaleFactor);
  };

  const fetchTemplatePreview = (previewUrl) => {
    if (!contentRef.current || !template || !template.id) return;

    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTimeRef.current;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (timeSinceLastRequest < 1500) {
      timeoutRef.current = setTimeout(() => {
        fetchTemplatePreview(previewUrl);
      }, 1500 - timeSinceLastRequest);
      return;
    }

    lastRequestTimeRef.current = now;

    $.ajax({
      url: `${window.location.origin}/templates/${template.id}`,
      dataType: "JSON",
      type: "GET",
      complete: function (data) {
        const responseData = data.responseJSON;
        if (!responseData || !contentRef.current) return;

        if (previewUrl) {
          $.ajax({
            url: previewUrl,
            type: "GET",
            success: function (html) {
              if (contentRef.current && wrapperRef.current) {
                contentRef.current.innerHTML = html;
                setIsLoading(false);

                const applyLayout = () => {
                  applyPreviewLayout(contentRef.current, wrapperRef.current);
                };

                requestAnimationFrame(() => {
                  requestAnimationFrame(applyLayout);
                });

                resizeObserverRef.current?.disconnect();
                resizeObserverRef.current = new ResizeObserver(() => {
                  applyLayout();
                });
                resizeObserverRef.current.observe(contentRef.current);
              }
            },
          });
        } else {
          setIsLoading(false);
        }
      },
    });
  };

  useEffect(() => {
    setIsLoading(true);
    fetchTemplatePreview(buildPreviewUrl);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, [template?.id, buildPreviewUrl]);

  const minHeightClass = props.children ? "min-h-[80px]" : "min-h-[30px]";

  return (
    <React.Fragment>
      <div
        ref={wrapperRef}
        className={`${minHeightClass} template-preview-wrapper relative overflow-hidden`}
        style={{ backgroundColor: options.styles?.["background-color"] ? options.styles?.["background-color"] : "white" }}
      >
        <div className="absolute flex origin-top transform-gpu w-full">
          <div
            data-src={buildPreviewUrl}
            ref={contentRef}
            className="w-full h-full template-preview-container"
          />
        </div>

        {isLoading ? (
          <div className="z-[1] absolute flex items-center justify-center inset-0 bg-black opacity-[0.7]">
            <span className="cf-loading-spinner" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-transparent hover:bg-white dark:hover:bg-slate-800 hover:bg-opacity-50 dark:hover:bg-opacity-50 cursor-pointer">
            {props.children}
          </div>
        )}
      </div>
    </React.Fragment>
  );
});

export default TemplatePreview;
